import React, { useState } from 'react';
import { useCart } from '../hooks/useCart';
import { supabase } from '../lib/supabase';
import { sendWhatsAppNotification } from '../lib/whatsapp';

interface Props { open: boolean; onClose: () => void; }
type Step = 'cart' | 'form' | 'success';

export default function CartDrawer({ open, onClose }: Props) {
  const { state, dispatch, total } = useCart();
  const [step,      setStep]      = useState<Step>('cart');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [pseudo,    setPseudo]    = useState('');
  const [phone,     setPhone]     = useState('');
  const [address,   setAddress]   = useState('');
  const [lat,       setLat]       = useState<number | null>(null);
  const [lng,       setLng]       = useState<number | null>(null);
  const [gpsStatus, setGpsStatus] = useState<'idle'|'loading'|'ok'|'error'>('idle');

  const fmt = (n: number) => n.toLocaleString('fr-FR');

  const requestGPS = () => {
    if (!navigator.geolocation) return setGpsStatus('error');
    setGpsStatus('loading');
    navigator.geolocation.getCurrentPosition(
      pos => { setLat(pos.coords.latitude); setLng(pos.coords.longitude); setGpsStatus('ok'); },
      ()  => setGpsStatus('error'),
      { timeout: 10000 }
    );
  };

  const handleOrder = async () => {
    if (!pseudo.trim())  return setError('Entrez un pseudonyme.');
    if (!phone.trim())   return setError('Entrez votre numéro.');
    if (!address.trim()) return setError('Entrez votre adresse.');
    setError(''); setLoading(true);
    try {
      const { data: orderData, error: orderErr } = await supabase
        .from('orders')
        .insert({ pseudo: pseudo.trim(), phone: phone.trim(), address: address.trim(), total, latitude: lat, longitude: lng })
        .select('id').single();
      if (orderErr || !orderData) throw new Error(orderErr?.message || 'Erreur commande');

      const { error: itemsErr } = await supabase.from('order_items').insert(
        state.items.map(i => ({ order_id: orderData.id, product_id: i.product.id, quantity: i.quantity, price: i.product.price }))
      );
      if (itemsErr) throw new Error(itemsErr.message);

      await sendWhatsAppNotification({
        pseudo: pseudo.trim(), phone: phone.trim(), address: address.trim(),
        items: state.items.map(i => ({ name: i.product.name, quantity: i.quantity, price: i.product.price, image_url: i.product.image_url })),
        total, orderId: orderData.id, latitude: lat ?? undefined, longitude: lng ?? undefined,
      });

      dispatch({ type: 'CLEAR' });
      setStep('success');
    } catch (e: any) {
      setError(e.message || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => { setStep('cart'); setPseudo(''); setPhone(''); setAddress(''); setError(''); setLat(null); setLng(null); setGpsStatus('idle'); }, 350);
  };

  const inp: React.CSSProperties = {
    width:'100%', background:'var(--surface2)', border:'1px solid var(--border)',
    color:'var(--text)', padding:'0.75rem 0.9rem', borderRadius:'10px',
    fontFamily:'Outfit,sans-serif', fontSize:'16px', outline:'none',
    WebkitAppearance:'none',
  };
  const lbl: React.CSSProperties = {
    display:'block', fontSize:'0.68rem', letterSpacing:'0.12em',
    textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.35rem',
  };

  return (
    <>
      <style>{`
        @keyframes slideInCart { from{transform:translateX(100%)} to{transform:translateX(0)} }
        @keyframes fadeCart    { from{opacity:0} to{opacity:1} }
      `}</style>

      {/* Overlay */}
      {open && <div onClick={handleClose} style={{
        position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:200,
        animation:'fadeCart 0.25s ease',
      }} />}

      {/* Drawer — plein écran sur mobile, 420px sur desktop */}
      <div style={{
        position:'fixed', top:0, right:0,
        width:'min(420px, 100vw)',
        height:'100dvh',
        background:'var(--surface)',
        borderLeft:'1px solid var(--border)',
        zIndex:201,
        display:'flex', flexDirection:'column',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition:'transform 0.32s cubic-bezier(0.4,0,0.2,1)',
        overscrollBehavior:'contain',
      }}>

        {/* ── SUCCESS ── */}
        {step === 'success' && (
          <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'2rem var(--page-px)', textAlign:'center', gap:'1rem',
            paddingBottom:'calc(2rem + env(safe-area-inset-bottom))' }}>
            <div style={{ fontSize:'3.5rem' }}>✅</div>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', fontWeight:400 }}>Commande confirmée !</h2>
            <p style={{ color:'var(--muted)', fontSize:'0.85rem', lineHeight:1.7 }}>
              Nous avons bien reçu votre commande.<br />Vous serez contacté·e sous peu.<br /><br />
              <em style={{ color:'var(--accent)' }}>Livraison discrète garantie.</em>
            </p>
            <button onClick={handleClose} style={{
              marginTop:'0.5rem', background:'none', border:'1px solid var(--border)',
              color:'var(--text)', padding:'0 1.5rem', height:'var(--touch-min)',
              borderRadius:'2rem', fontSize:'0.85rem', width:'100%',
            }}>
              Continuer mes achats
            </button>
          </div>
        )}

        {step !== 'success' && (
          <>
            {/* Header */}
            <div style={{
              padding:'0 var(--page-px)',
              height:'56px',
              display:'flex', justifyContent:'space-between', alignItems:'center',
              borderBottom:'1px solid var(--border)', flexShrink:0,
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                {step === 'form' && (
                  <button onClick={() => setStep('cart')} style={{
                    background:'none', border:'none', color:'var(--muted)',
                    fontSize:'0.85rem', padding:'0',
                    height:'var(--touch-min)', display:'flex', alignItems:'center', gap:'0.25rem',
                  }}>← Retour</button>
                )}
                {step === 'cart' && (
                  <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.25rem', fontWeight:400 }}>Panier</h2>
                )}
                {step === 'form' && (
                  <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.25rem', fontWeight:400 }}>Livraison</h2>
                )}
              </div>
              <button onClick={handleClose} style={{
                background:'none', border:'none', color:'var(--muted)',
                width:'var(--touch-min)', height:'var(--touch-min)',
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem',
              }}>✕</button>
            </div>

            {/* ── CART ── */}
            {step === 'cart' && (
              <>
                <div style={{ flex:1, overflowY:'auto', WebkitOverflowScrolling:'touch' as any, padding:'0.5rem var(--page-px)' }}>
                  {state.items.length === 0
                    ? <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:'0.6rem', color:'var(--muted)' }}>
                        <span style={{ fontSize:'2.5rem' }}>🛒</span>
                        <p style={{ fontSize:'0.85rem' }}>Votre panier est vide</p>
                      </div>
                    : state.items.map(item => (
                        <div key={item.product.id} style={{
                          display:'flex', alignItems:'center', gap:'0.75rem',
                          padding:'0.85rem 0', borderBottom:'1px solid var(--border)',
                        }}>
                          {/* Thumb */}
                          <div style={{
                            width:'3rem', height:'3rem', borderRadius:'8px',
                            background:'var(--surface2)', overflow:'hidden', flexShrink:0,
                            display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.4rem',
                          }}>
                            {item.product.image_url
                              ? <img src={item.product.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                              : '📦'
                            }
                          </div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:'0.83rem', marginBottom:'0.15rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.product.name}</div>
                            <div style={{ fontSize:'0.75rem', color:'var(--accent)' }}>{fmt(item.product.price * item.quantity)} FCFA</div>
                          </div>
                          {/* Qty */}
                          <div style={{ display:'flex', alignItems:'center', gap:'0', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'2rem', flexShrink:0 }}>
                            <button onClick={() => dispatch({ type: 'DEC', productId: item.product.id })}
                              style={{ width:'2rem', height:'2rem', background:'none', border:'none', color:'var(--text)', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>−</button>
                            <span style={{ fontSize:'0.82rem', minWidth:'1.2rem', textAlign:'center' }}>{item.quantity}</span>
                            <button onClick={() => dispatch({ type: 'INC', productId: item.product.id })}
                              style={{ width:'2rem', height:'2rem', background:'none', border:'none', color:'var(--text)', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>+</button>
                          </div>
                          <button onClick={() => dispatch({ type: 'REMOVE', productId: item.product.id })}
                            style={{ background:'none', border:'none', color:'var(--muted)', width:'var(--touch-min)', height:'var(--touch-min)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.9rem', flexShrink:0 }}>✕</button>
                        </div>
                      ))
                  }
                </div>

                {state.items.length > 0 && (
                  <div style={{
                    padding:'1rem var(--page-px)',
                    borderTop:'1px solid var(--border)', flexShrink:0,
                    paddingBottom:'calc(1rem + env(safe-area-inset-bottom))',
                  }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.9rem' }}>
                      <span style={{ fontSize:'0.85rem', color:'var(--muted)' }}>Total</span>
                      <strong style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.15rem', color:'var(--accent2)' }}>{fmt(total)} FCFA</strong>
                    </div>
                    <button onClick={() => setStep('form')} style={{
                      width:'100%', height:'var(--touch-min)',
                      background:'var(--accent)', color:'var(--bg)',
                      border:'none', borderRadius:'2rem',
                      fontSize:'0.9rem', fontWeight:500,
                    }}>
                      Commander →
                    </button>
                  </div>
                )}
              </>
            )}

            {/* ── FORM ── */}
            {step === 'form' && (
              <>
                <div style={{ flex:1, overflowY:'auto', WebkitOverflowScrolling:'touch' as any, padding:'1rem var(--page-px)', display:'flex', flexDirection:'column', gap:'0.9rem' }}>
                  <p style={{ fontSize:'0.78rem', color:'var(--muted)', lineHeight:1.6, background:'var(--surface2)', padding:'0.65rem 0.8rem', borderRadius:'8px' }}>
                    🔒 Vos infos restent confidentielles. Le pseudo sera utilisé à la livraison.
                  </p>

                  <div>
                    <label style={lbl}>Pseudonyme</label>
                    <input style={inp} value={pseudo} onChange={e => setPseudo(e.target.value)} placeholder="Ex : Aigle123" maxLength={50} />
                  </div>
                  <div>
                    <label style={lbl}>Téléphone</label>
                    <input style={inp} type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+226 70 00 00 00" />
                  </div>
                  <div>
                    <label style={lbl}>Adresse de livraison</label>
                    <textarea style={{ ...inp, resize:'none' }} rows={2} value={address} onChange={e => setAddress(e.target.value)} placeholder="Secteur, quartier, point de repère…" />
                  </div>

                  {/* GPS */}
                  <div>
                    <label style={lbl}>Localisation GPS <span style={{ opacity:0.6, fontStyle:'italic' }}>(optionnel)</span></label>
                    <button type="button" onClick={requestGPS} disabled={gpsStatus === 'loading' || gpsStatus === 'ok'}
                      style={{
                        width:'100%', height:'var(--touch-min)', borderRadius:'10px',
                        border:`1px solid ${gpsStatus === 'ok' ? 'var(--success)' : gpsStatus === 'error' ? 'var(--danger)' : 'var(--border)'}`,
                        background: gpsStatus === 'ok' ? 'rgba(112,196,144,0.08)' : 'var(--surface2)',
                        color: gpsStatus === 'ok' ? 'var(--success)' : gpsStatus === 'error' ? 'var(--danger)' : 'var(--muted)',
                        fontSize:'0.82rem', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem',
                      }}>
                      {gpsStatus === 'idle'    && <><span>📍</span> Partager ma position</>}
                      {gpsStatus === 'loading' && <><span>⏳</span> Localisation…</>}
                      {gpsStatus === 'ok'      && <><span>✅</span> Position partagée {lat?.toFixed(4)}, {lng?.toFixed(4)}</>}
                      {gpsStatus === 'error'   && <><span>⚠️</span> GPS indisponible</>}
                    </button>
                  </div>

                  {/* Récap */}
                  <div style={{ background:'var(--surface2)', borderRadius:'10px', padding:'0.8rem 0.9rem', fontSize:'0.78rem' }}>
                    {state.items.map(i => (
                      <div key={i.product.id} style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.3rem', color:'var(--muted)' }}>
                        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, marginRight:'0.5rem' }}>{i.product.name} ×{i.quantity}</span>
                        <span style={{ flexShrink:0 }}>{fmt(i.product.price * i.quantity)} F</span>
                      </div>
                    ))}
                    <div style={{ borderTop:'1px solid var(--border)', marginTop:'0.5rem', paddingTop:'0.5rem', display:'flex', justifyContent:'space-between', color:'var(--accent2)', fontWeight:500 }}>
                      <span>Total</span><span>{fmt(total)} FCFA</span>
                    </div>
                  </div>

                  <p style={{ fontSize:'0.72rem', color:'var(--muted)' }}>💳 Paiement à la livraison</p>
                  {error && <p style={{ color:'var(--danger)', fontSize:'0.8rem', background:'rgba(217,112,112,0.08)', padding:'0.5rem 0.7rem', borderRadius:'8px' }}>{error}</p>}
                </div>

                {/* CTA fixe */}
                <div style={{ padding:'1rem var(--page-px)', borderTop:'1px solid var(--border)', flexShrink:0, paddingBottom:'calc(1rem + env(safe-area-inset-bottom))' }}>
                  <button onClick={handleOrder} disabled={loading} style={{
                    width:'100%', height:'var(--touch-min)',
                    background: loading ? 'var(--muted)' : 'var(--accent)',
                    color:'var(--bg)', border:'none', borderRadius:'2rem',
                    fontSize:'0.9rem', fontWeight:500,
                  }}>
                    {loading ? 'Envoi en cours…' : 'Confirmer la commande'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}